import { zodResolver } from '@hookform/resolvers/zod';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, Grid, Switch, TextField, Typography,
} from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useEffect } from 'react';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { smallDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { LeadStatusService } from '../services/lead-status.service';
import { LeadStatusRequest } from '../services/requests/lead-status.request';
import { LeadStatusResponse } from '../services/responses/lead-status.response';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { firstErrorMessage } from '@functions/first-error-message';

interface Props {
    open: boolean;
    edit: LeadStatusResponse | null;
    handleModal: () => void;
    getAll: () => void;
    clearSearch: () => void;
}

export const LeadStatusModal = ({ open, edit, handleModal, getAll, clearSearch }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();
    const service = new LeadStatusService();

    const schema = z.object({
        name: z.string().min(1, t('leadStatus.nomeObrigatorio')).max(50),
        color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, t('leadStatus.corObrigatoria')),
        isDefault: z.boolean(),
    });

    const form = useForm<LeadStatusRequest>({
        resolver: zodResolver(schema),
        defaultValues: { name: '', color: '#3B82F6', isDefault: false },
    });

    const { control, handleSubmit, reset, watch, formState: { errors } } = form;
    const colorValue = watch('color');

    useEffect(() => {
        if (open && edit) {
            reset({ name: edit.name, color: edit.color, isDefault: edit.isDefault });
        } else if (open) {
            reset({ name: '', color: '#3B82F6', isDefault: false });
        }
    }, [open]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);
        }
    }, [errors]);

    const saveChanges = async (data: LeadStatusRequest) => {
        setLoading(dispatch, true);
        try {
            if (edit) {
                await service.update(edit.id, data);
                showAlert(t('general.atualizacao'), 'success', 2500);
                getAll();
            } else {
                await service.create(data);
                showAlert(t('general.cadastro'), 'success', 2500);
                clearSearch();
            }
            handleModal();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return (
        <Dialog open={open} onClose={handleModal} fullWidth maxWidth="sm" scroll="paper">
            <DialogTitle>{edit ? t('general.editar') : t('general.adicionar')}</DialogTitle>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(saveChanges)}>
                    <DialogContent style={smallDialog}>
                        <Grid container spacing={2}>
                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('general.nome')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.Input type="text" autoComplete="off" size="medium" name="name" autoFocus />
                                    <Form.ErrorMessage field="name" />
                                </FormControl>
                            </Form.Field>
                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('leadStatus.cor')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Controller
                                        name="color"
                                        control={control}
                                        render={({ field }) => (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <TextField
                                                    {...field}
                                                    size="small"
                                                    placeholder="#RRGGBB"
                                                    error={!!errors.color}
                                                    sx={{ flex: 1 }}
                                                />
                                                <input
                                                    type="color"
                                                    value={colorValue?.match(/^#[0-9A-Fa-f]{6}$/) ? colorValue : '#3B82F6'}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', background: 'none', padding: 0 }}
                                                />
                                            </Box>
                                        )}
                                    />
                                    <Form.ErrorMessage field="color" />
                                </FormControl>
                            </Form.Field>
                            <Form.Field size={12}>
                                <Controller
                                    name="isDefault"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={field.value}
                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                    color="success"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body2">{t('leadStatus.padrao')}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t('leadStatus.padraoHelp')}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    )}
                                />
                            </Form.Field>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box></Box>
                        <Box>
                            <MDButton sx={{ marginRight: '5px' }} variant="gradient" color="dark" onClick={handleModal}>
                                {t('general.cancelar')}
                            </MDButton>
                            <MDButton variant="gradient" color="success" type="submit">
                                {t('general.salvar')}
                            </MDButton>
                        </Box>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
